import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Trash, DownloadSimple } from "@phosphor-icons/react";
import { Header, F, IconBtn, Modal } from "@/components/Clients";

const empty = { name: "", category: "Return", client_id: "", description: "", file_data: null, file_mime: null };

const Documents = ({ readOnly = false }) => {
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/documents").then((r) => setRows(r.data.documents));
  useEffect(() => {
    load();
    if (!readOnly) api.get("/clients").then((r) => setClients(r.data.clients));
  }, [readOnly]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) return toast.error("Max 4MB per file (MVP)");
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.toString().split(",")[1];
      setForm((p) => ({ ...p, file_data: base64, file_mime: f.type, name: p.name || f.name }));
    };
    reader.readAsDataURL(f);
  };

  const save = async (e) => {
    e.preventDefault();
    setUploading(true);
    try {
      await api.post("/documents", { ...form, client_id: form.client_id || null });
      toast.success("Uploaded");
      setShowForm(false);
      setForm(empty);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setUploading(false);
    }
  };

  const download = async (d) => {
    const r = await api.get(`/documents/${d.id}`);
    const doc = r.data;
    if (!doc.file_data) return toast.error("No file attached");
    const link = document.createElement("a");
    link.href = `data:${doc.file_mime || "application/octet-stream"};base64,${doc.file_data}`;
    link.download = doc.name;
    link.click();
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete ${d.name}?`)) return;
    await api.delete(`/documents/${d.id}`);
    load();
  };

  const openNew = () => { setForm(empty); setShowForm(true); };

  return (
    <div>
      <Header
        title="Document Vault"
        subtitle="Returns, invoices, statements, certificates"
        onNew={readOnly ? undefined : openNew}
        newLabel="Upload"
        testId="document"
      />

      <div className="mt-6 border border-[#E8EAE8] bg-white">
        <div className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-[#E8EAE8] text-[11px] uppercase tracking-widest text-[#5C6B64] font-semibold">
          <div className="col-span-4">Name</div>
          <div className="col-span-2">Category</div>
          <div className="col-span-3">Client</div>
          <div className="col-span-2 font-mono-data">Uploaded</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>
        {rows.length === 0 && <div className="p-6 text-sm text-[#5C6B64]">No documents yet.</div>}
        {rows.map((d) => (
          <div key={d.id} data-testid={`doc-row-${d.id}`} className="grid grid-cols-12 gap-4 px-5 py-4 border-b last:border-b-0 border-[#E8EAE8] items-center">
            <div className="col-span-4">
              <div className="text-[#0F2E22] font-medium">{d.name}</div>
              {d.description && <div className="text-xs text-[#5C6B64] mt-0.5">{d.description}</div>}
            </div>
            <div className="col-span-2 text-sm">{d.category}</div>
            <div className="col-span-3 text-sm">{d.client_name || "—"}</div>
            <div className="col-span-2 font-mono-data text-xs">{(d.created_at || "").slice(0, 10)}</div>
            <div className="col-span-1 flex justify-end gap-2">
              <IconBtn onClick={() => download(d)} testId={`dl-${d.id}`}><DownloadSimple size={14} /></IconBtn>
              {!readOnly && <IconBtn onClick={() => remove(d)} testId={`del-doc-${d.id}`}><Trash size={14} /></IconBtn>}
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <Modal title="Upload Document" onClose={() => setShowForm(false)}>
          <form onSubmit={save} className="space-y-3">
            <F label="Name" value={form.name} onChange={set("name")} required testId="form-name" />
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-[#5C6B64]">Category</span>
                <select data-testid="form-category" value={form.category} onChange={set("category")} className="mt-1 w-full border border-[#E8EAE8] px-3 py-2 text-sm bg-white">
                  {["Invoice", "Return", "Statement", "Certificate", "Contract", "Other"].map((x) => <option key={x}>{x}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-[#5C6B64]">Client</span>
                <select data-testid="form-client" value={form.client_id} onChange={set("client_id")} className="mt-1 w-full border border-[#E8EAE8] px-3 py-2 text-sm bg-white">
                  <option value="">— None —</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </label>
            </div>
            <F label="Description" value={form.description} onChange={set("description")} testarea testId="form-desc" />
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-[#5C6B64]">File (max 4MB)</span>
              <input type="file" onChange={onFile} data-testid="form-file" className="mt-1 block w-full text-sm" />
            </label>
            <button type="submit" disabled={uploading} data-testid="save-doc-btn" className="w-full py-3 bg-[#0F2E22] text-white text-sm tracking-widest uppercase hover:opacity-90 disabled:opacity-50">
              {uploading ? "Uploading…" : "Save"}
            </button>
          </form>
        </Modal>
      )}
    </div>
  );
};

export default Documents;
